import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { IconSet } from 'stile-shared/src/components/2024/common/IconSet/IconSet';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import PedagogyHeader from 'stile-shared/src/components/2024/common/PedagogyHeader';
import PedagogyWithinStileCard from 'stile-shared/src/components/2024/common/PedagogyWithinStileCard';
import { SubNav, SubNavItem } from 'stile-shared/src/components/2024/common/SubNav';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

const ExamplesSection = styled<typeof Section>(Section)`
  margin-top: 0 !important;
  grid-column: span 12;

  .content {
    padding-top: 0 !important;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      padding-top: 0 !important;
      margin-bottom: 69px !important;
      margin: 0 25px;
    }
  }

  position: relative;

  h6 {
    color: var(--stile-green-3);
    margin: 0;
    margin-bottom: 10px;
    grid-column: span 12;
    @media (max-width: 768px) {
      margin-bottom: 0 !important;
    }
  }

  .grid-layout {
    margin-top: 0 !important;
    grid-column: 1 / span 12;
  }

  > .grid-layout {
    align-items: center;
    margin-bottom: 150px;
    @media (max-width: 768px) {
      margin-bottom: 80px;
    }
  }
`;

export { Head } from 'templates/2024/Head';

function DifferentiatedInstructionPage(props: PageProps) {
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  const subNavItems: SubNavItem[] = [
    {
      label: '5E Instructional Model',
      url: '/pedagogies/5e-instructional-model/',
    },
    {
      label: 'Phenomena-based Learning',
      url: '/pedagogies/phenomena-based-learning/',
    },
    {
      label: 'Three-dimensional Learning',
      url: '/pedagogies/three-dimensional-learning/',
    },
    {
      label: 'SOLO Taxonomy',
      url: '/pedagogies/solo-taxonomy/',
    },
    {
      label: 'Universal Design for Learning',
      url: '/pedagogies/universal-design/',
    },
    {
      label: 'Explicit Instruction',
      url: '/pedagogies/explicit-instruction/',
    },
    {
      label: 'Inquiry-based Learning',
      url: '/pedagogies/inquiry-based-learning/',
    },
    {
      label: 'Cognitive Load Theory',
      url: '/pedagogies/cognitive-load-theory/',
    },
    {
      label: 'Differentiated Instruction',
      url: '/pedagogies/differentiated-instruction/',
      isActive: true,
    },
  ];

  return (
    <PageLayout {...props}>
      <SubNav items={subNavItems} />

      <PedagogyHeader
        title="Differentiated instruction"
        description="Differentiated instruction is about challenging every student at an appropriate level. Teachers support the progress of all students by adjusting the content they learn, the process used to understand it, and the product that demonstrates their understanding. Formative assessment helps teachers select appropriate intervention strategies."
        iconBullets={[
          {
            iconSrc: IconSet['lightonIcon0404'],
            title: 'Initially developed by',
            text: 'Carol Ann Tomlinson, University of Virginia, USA (1990s).',
          },
          {
            iconSrc: IconSet['conflictIcon0505'],
            title: 'Effectiveness',
            text: (
              <>
                Asriadi et al.’s 2023 study
                <Link url="https://files.eric.ed.gov/fulltext/EJ1411934.pdf" openInNewTab>
                  ‘Does differentiated instruction affect learning outcome? Systematic review and
                  meta-anlysis’
                </Link>
                found that differentiated instruction had a significant impact over traditional
                learning methods, with a Standardized Mean Difference of 1.02 in the science
                classroom.
              </>
            ),
          },
          {
            iconSrc: IconSet['glossaryIcon0705'],
            title: 'Further reading',
            text: (
              <>
                <Link
                  url="https://rutamaestra.santillana.com.co/wp-content/uploads/2020/01/The-Differentiated-Classroom-Responding-to-the-Needs.pdf"
                  openInNewTab
                >
                  The Differentiated Classroom
                </Link>
                , by Carol Ann Tomlinson.
              </>
            ),
          },
          {
            iconSrc: IconSet['locationIcon0704'],
            title: "Where you'll find it in Stile",
            text: "Complete customisability allows teachers to tailor lessons to students' specific needs. Frequent, automatically marked formative assessment tasks allow teachers to track student progress and identify needs.",
          },
        ]}
      />

      <ExamplesSection>
        <h6>Examples within Stile</h6>
        <Grid>
          <PedagogyWithinStileCard
            id="meeting-students"
            title="Meeting students where they are"
            description="Lessons are scaffolded from lower- to higher-order thinking, creating multiple entry points for learners based on their ability."
            url="http://stileapp.com/go/differentiated-example-1"
            subtitle={
              <div>
                <em>Design a hand warmer</em> lesson
                <br />
                in the Physical and Chemical Change unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/differentiation_scaffold_US.png"
                alt="Text: Create a design for a hand warmer. Image: Photo of hand warmers with diagrams of endothermic and exothermic reactions, plus example questions asking students to classify the reaction type and propose a design solution."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="tailored-lessons"
            title="Tailored lessons released to select student groups"
            description="Any Stile lesson can be copied, then edited to suit student needs: reducing complexity, simplifying language, adding personal points of interest and so on. Modified lessons can be released to select groups of students."
            url="http://stileapp.com/go/differentiated-example-2"
            subtitle={
              <div>
                <em>Introduction: How can your gut influence your mood?</em> lesson
                <br />
                in the Nervous System unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/differentiation_customise.png"
                alt="Text: The case of the anxious mice. Image: Differentiation options for assigning lessons, showing a cartoon mouse and a lesson selection menu with the ability to release tasks to specific student groups."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="opportunities-for-extension"
            title="Opportunities for extension"
            description="Every lesson ends with either a singular Challenge Question or an entire “Going Further” section where students have an opportunity to extend themselves beyond the lesson's learning goal."
            url="http://stileapp.com/go/differentiated-example-3"
            subtitle={
              <div>
                <em>Moons of Jupiter and Saturn</em> lesson
                <br />
                in the Our Place in Space unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/differentiation_challenge_US.png"
                alt="Text: Predict which moons in our Solar System may be capable of supporting life. Image: Challenge question with a writing space and photos of four moons."
                objectFit="contain"
              />
            }
          />
        </Grid>
      </ExamplesSection>

      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: "Inside Stile's Curriculum",
            description: 'How we make the best science lessons in the known universe.',
            url: '/what-is-stile/middle-school-curriculum/',
          },
          {
            title: "What's Included",
            description:
              'A complete, comprehensive curriculum — used, loved, and perfected by thousands of teachers.',
            url: '/what-is-stile/whats-included/',
          },
          {
            title: 'Why HQIM?',
            description: 'Why High-Quality Instructional Materials make all the difference.',
            url: '/why-choose-stile/high-quality-instructional-materials/',
          },
        ]}
      />
    </PageLayout>
  );
}

export default DifferentiatedInstructionPage;
